import { Component, HostListener } from '@angular/core';
import { LoadSearchComponent } from '@app/loads/load-search/load-search.component';
import { LoadSearchFiltersDialogComponent } from '@app/loads/load-search-filters-dialog/load-search-filters-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { AppService } from '@app/app.service';
import { LoadSearch } from '@app/loads/models/load-search.model';
import { AuthService } from '@app/auth/auth.service';
import { Router } from '@angular/router';
import { LoadingOverlayService } from '@app/shared/loading-overlay/loading-overlay.service';
import { UserPlan } from '@app/auth/user.model';
import { UserSettings } from '@app/auth/user.model';
import { RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-load-searches',
  imports: [
    LoadSearchComponent,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    RouterModule,
    MatListModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './load-searches.component.html',
  styleUrl: './load-searches.component.scss',
})
export class LoadSearchesComponent {
  loadSearches: LoadSearch[] = [];
  selectedIndex = 0;

  truckToMatch: any = null;

  loading = false;

  userPlan: UserPlan | null = null;
  userSettings: UserSettings | null = null;
  defaultSearchRadius: number | null = null;

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private dialog: MatDialog,
    private appService: AppService,
    private authService: AuthService,
    private router: Router,
    private loadingOverlayService: LoadingOverlayService
  ) { }

  ngOnInit(): void {

    if (!this.authService.userCanSearchLoads()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    this.userPlan = this.authService.getUserPlanFromStorage();
    this.userSettings = this.authService.getUserSettingsFromStorage();
    this.defaultSearchRadius = this.userSettings?.default_search_radius_miles ?? 250;

    this.getLoadSearches();

    this.truckToMatch = history.state.truckToMatch;

    if (this.truckToMatch) {
      this.matchLoad(this.truckToMatch);
    }

  }

  getLoadSearches(selectedSearchId?: number): void {

    this.loading = true;
    this.loadingOverlayService.show();

    this.appService.get('loads/searches').subscribe({
      next: (res) => {
        this.loadSearches = res.data ?? [];

        if (selectedSearchId) {
          const index = this.loadSearches.findIndex(
            (search) => search.id === selectedSearchId
          );

          if (index !== -1) {
            this.selectedIndex = index;
          }
        } else {
          const defaultIndex = this.loadSearches.findIndex(
            (search) => search.is_default
          );

          this.selectedIndex = defaultIndex !== -1 ? defaultIndex : 0;
        }

        if (res.time < 0.5) {
          setTimeout(() => {
            this.loadingOverlayService.hide();
          }, 500);
        } else {
          this.loadingOverlayService.hide();
        }

        this.loading = false;
      },
      error: (err) => {
        this.loadSearches = [];
        this.appService.showSnackBar(err.error.message, 'error');
        this.loadingOverlayService.hide();
        this.loading = false;
      },
    });
  }

  // Create a new load search
  openFiltersDialog(filters: any = {}): void {

    const maxSavedLoadSearches = this.userPlan?.max_saved_load_searches ?? 0;
    
    if (this.loadSearches.length >= maxSavedLoadSearches) {
      this.appService.warningSnackBar(`Your plan allows a maximum of ${maxSavedLoadSearches} saved load searches.`);
      return;
    }

    const dialogRef = this.dialog.open(LoadSearchFiltersDialogComponent, {
      width: '550px',
      maxWidth: '95vw',
      data: { filters: filters },
    });

    dialogRef.afterClosed().subscribe((result: LoadSearch | undefined) => {
      if (!result) {
        return;
      }

      this.loadSearches.push(result);
      this.selectedIndex = this.loadSearches.length - 1;

      if(this.isMobile) {
        this.router.navigate(['/loads/searches/' + result.id]);
      }

    });
  }

  deleteSearch(search: LoadSearch, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    this.appService.delete(`loads/searches/${search.id}`).subscribe({
      next: (res) => {

        const deletedIndex = this.loadSearches.findIndex(
          (item) => item.id === search.id
        );

        this.loadSearches = this.loadSearches.filter(
          (item) => item.id !== search.id
        );

        if (this.loadSearches.length === 0) {
          this.selectedIndex = 0;
          return;
        }

        if (this.selectedIndex >= this.loadSearches.length) {
          this.selectedIndex = this.loadSearches.length - 1;
        } else if (deletedIndex < this.selectedIndex) {
          this.selectedIndex = this.selectedIndex - 1;
        }
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  matchLoad(truck: any): void {
    if (!truck) {
      return;
    }

    const filters = {
      origin: truck.origin,
      origin_latitude: truck.origin_latitude,
      origin_longitude: truck.origin_longitude,
      origin_radius: this.defaultSearchRadius,
    };

    this.openFiltersDialog(filters);

    // Clear state
    history.replaceState({}, '', this.router.url);
  }

}